/**
 * Central registry of PostHog feature flag keys.
 *
 * All feature flags belong here. Keys are the kebab-case form of the
 * identifier with a `web-` prefix so web app flags are easy to spot in the
 * PostHog dashboard alongside flags owned by other clients. Identifiers use
 * camelCase and contain an auxiliary verb (`is`, `has`, `should`...) when the
 * flag is a boolean.
 *
 * Kill-switch convention: flags named `<page>IsClosed` /
 * `<feature>InMaintenance` default to `false` (the page works), so tests
 * render the working path without having to stub the flag service.
 */
export enum PostHogFeatureFlags {
  optaStatsPageIsClosed = 'web-opta-stats-page-is-closed',
  playerComparisonPageIsClosed = 'web-player-comparison-page-is-closed',
  playerProfilePageIsClosed = 'web-player-profile-page-is-closed',
  myTeamIsInMaintenance = 'web-my-team-is-in-maintenance',
  pointsPageIsClosed = 'web-points-page-is-closed',
  fplSeasonIsClosed = 'web-fpl-season-is-closed',
  miniLeagueIsClosed = 'web-mini-league-is-closed',
  priceChangesIsClosed = 'web-price-changes-is-closed',
  communityVoteIsLive = 'web-community-vote-is-live',
  sitewideBanner = 'web-sitewide-banner',
  newNotificationPreferencesFlowIsLive = 'web-new-notification-preferences-flow-is-live',
  appUpsellIsLive = 'web-app-upsell-is-live',
  snackAdvertisingIsLive = 'web-snack-advertising-is-live',
  chipStrategyIsLive = 'web-chip-strategy-is-live',
  chipStrategyIsClosed = 'web-chip-strategy-is-closed',
  // Named to match the app's `app-rivals-phase-1` rather than this file's
  // identifier-derived keys, so both rollouts read as one in PostHog.
  rivalsPhase1IsLive = 'web-rivals-phase-1'
}

/**
 * Flag values forced for app-review demo sessions (see useDemoApiRouting).
 *
 * The demo user is served mid-season data by a separate demo backend, but
 * PostHog flags are evaluated client-side and would otherwise still apply
 * the out-of-season kill switches to them. Every season-dependent flag must
 * appear here with its mid-season value (`*IsClosed`/`*InMaintenance` off,
 * `*IsLive` on) so demo sessions always see the in-season experience.
 * Flags that are not season-dependent (e.g. sitewideBanner) are deliberately
 * absent and behave normally for demo sessions.
 */
export const DEMO_SESSION_FLAG_OVERRIDES: Partial<
  Record<PostHogFeatureFlags, boolean>
> = {
  [PostHogFeatureFlags.optaStatsPageIsClosed]: false,
  [PostHogFeatureFlags.playerComparisonPageIsClosed]: false,
  [PostHogFeatureFlags.playerProfilePageIsClosed]: false,
  [PostHogFeatureFlags.myTeamIsInMaintenance]: false,
  [PostHogFeatureFlags.pointsPageIsClosed]: false,
  [PostHogFeatureFlags.fplSeasonIsClosed]: false,
  [PostHogFeatureFlags.miniLeagueIsClosed]: false,
  [PostHogFeatureFlags.priceChangesIsClosed]: false,
  [PostHogFeatureFlags.chipStrategyIsClosed]: false,
  [PostHogFeatureFlags.communityVoteIsLive]: true
};
